import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Copy, Heart, ShieldCheck,
  Settings as SettingsIcon, MessageCircle, FileText, ArrowLeft, ChevronRight,
  X, Check, Loader2, Send, LogOut
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useUserData, useSubscription } from '../hooks/useApi';
import { AppLang } from '../i18n';
import { ReferralPage } from './ReferralPage';
import { AccessPage } from './AccessPage';
import { SupportChatPage } from './SupportChatPage';
import './ProfilePage.css';
import './SupportPage.css';
import { openTgLink } from '../lib/openTgLink';

interface ProfilePageProps {
  user: any;
  lang: AppLang;
  subPage: string | null;
  onNavigate: (page: string) => void;
  onBack: () => void;
  onLanguageChange: (lang: AppLang) => void;
  onShowOnboarding?: () => void;
  onTabChange: (tab: any) => void;
}

export const ProfilePage = ({ user, lang, subPage, onNavigate, onBack }: ProfilePageProps) => {
  const { data: userData } = useUserData();
  const { data: subData } = useSubscription();
  const [copiedId, setCopiedId] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [copiedSub, setCopiedSub] = useState(false);
  const subUrl = subData?.subscription?.sub_url || '';
  // Зашифрованная happ://crypt5 ссылка (конфиг скрыт). Фолбэк — happ://add.
  const happLink = subData?.subscription?.happ_link || (subUrl ? `happ://add/${subUrl}` : '');
  const subLoading = !subData;

  const handleCopySub = () => {
    if (!happLink) return;
    navigator.clipboard.writeText(happLink).then(() => {
      setCopiedSub(true);
      setTimeout(() => setCopiedSub(false), 2000);
    });
  };

  const handleSendSub = () => {
    if (!happLink) return;
    const tg = (window as any).Telegram?.WebApp;
    const shareUrl = 'https://t.me/share/url?url=' + encodeURIComponent(happLink);
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, '_blank');
  };

  const handleCopyId = useCallback(() => {
    const id = String(user?.id || '');
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  }, [user?.id]);

  // Sub-pages
  if (subPage === 'referral') {
    return <ReferralPage user={user} lang={lang} onBack={onBack} />;
  }
  if (subPage === 'access') {
    return <AccessPage user={user} lang={lang} subUrl={subUrl} onBack={onBack} />;
  }
  if (subPage === 'support-chat') {
    return <SupportChatPage lang={lang} onBack={onBack} />;
  }
  if (subPage === 'history') {
    return (
      <div className="pp">
        <div className="pp-sub-header">
          <button className="pp-back" onClick={onBack}><ArrowLeft size={20} /></button>
          <h2 className="pp-sub-title">Подписка</h2>
        </div>
        <div className="pp-sub-content">
          <div style={{ padding: '32px 16px', textAlign: 'center', opacity: 0.6 }}>
            Управление подпиской доступно на главной странице
          </div>
        </div>
      </div>
    );
  }
  if (subPage === 'terms') {
    return (
      <div className="pp">
        <div className="pp-sub-header">
          <button className="pp-back" onClick={onBack}><ArrowLeft size={20} /></button>
          <h2 className="pp-sub-title">Документы</h2>
        </div>
        <div className="pp-sub-content">
          <div className="pp-list" style={{ padding: '0 16px' }}>
            <button className="pp-list-item" onClick={() => {
              const tg = (window as any).Telegram?.WebApp;
              const url = 'https://patrik-service.com/terms.html';
              if (tg?.openLink) tg.openLink(url);
              else window.open(url, '_blank');
            }}>
              <FileText size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Пользовательское соглашение</span>
                <span className="pp-item-desc">Условия использования сервиса</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
            <button className="pp-list-item" onClick={() => {
              const tg = (window as any).Telegram?.WebApp;
              const url = 'https://patrik-service.com/privacy.html';
              if (tg?.openLink) tg.openLink(url);
              else window.open(url, '_blank');
            }}>
              <ShieldCheck size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Политика конфиденциальности</span>
                <span className="pp-item-desc">Защита персональных данных</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pp">
      <motion.div className="pp-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {/* User card */}
        <div className="pp-user-card">
          <div className="pp-avatar">
            {user?.photo_url ? (
              <img src={user.photo_url} alt="" className="pp-avatar-img" />
            ) : (
              <div className="pp-avatar-placeholder"><User size={28} /></div>
            )}
          </div>
          <div className="pp-user-info">
            <span className="pp-user-name">{user?.first_name || 'User'}</span>
            <span className="pp-user-id">id: {user?.id || '000000'}</span>
          </div>
          <button className="pp-copy-id" onClick={handleCopyId}>
            {copiedId ? '✓' : <Copy size={18} />}
          </button>
        </div>

        {/* Settings section */}
        <div className="pp-section">
          <h3 className="pp-section-title">Настройки профиля</h3>
          <div className="pp-list">
            <button className="pp-list-item" onClick={() => onNavigate('referral')}>
              <Heart size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Реферальная программа</span>
                <span className="pp-item-desc">Получайте бонусы за приглашения</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
            <button className="pp-list-item" onClick={() => onNavigate('access')}>
              <ShieldCheck size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Сохранение доступа</span>
                <span className="pp-item-desc">
                  {userData?.email ? `Веб-доступ: ${userData.email}` : 'Настроить веб-доступ'}
                </span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
          </div>
        </div>

        {/* Support section */}
        <div className="pp-section">
          <h3 className="pp-section-title">Поддержка</h3>
          <div className="pp-list">
            <button className="pp-list-item" onClick={() => setShowQr(true)}>
              <SettingsIcon size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Установка на другом устройстве</span>
                <span className="pp-item-desc">QR-код вашей подписки</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
            <button className="pp-list-item" onClick={() => {
              const tg = (window as any).Telegram?.WebApp;
              openTgLink('https://t.me/Patrik_vpn_support');
            }}>
              <MessageCircle size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Связаться с поддержкой</span>
                <span className="pp-item-desc">Написать в Telegram</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
            <button className="pp-list-item" onClick={() => onNavigate('terms')}>
              <FileText size={20} className="pp-item-icon" />
              <div className="pp-item-info">
                <span className="pp-item-label">Документы</span>
                <span className="pp-item-desc">Соглашение и конфиденциальность</span>
              </div>
              <ChevronRight size={18} className="pp-item-arrow" />
            </button>
          </div>
        </div>

        {/* Выход из аккаунта — только для веб-входа (в Telegram не нужен) */}
        {!(window as any).Telegram?.WebApp?.initData && (
          <div className="pp-section">
            <div className="pp-list">
              <button className="pp-list-item" onClick={() => {
                localStorage.removeItem('hits_jwt');
                localStorage.removeItem('hits_user');
                window.location.href = '/';
              }}>
                <LogOut size={20} className="pp-item-icon" style={{ color: '#ff6b7a' }} />
                <div className="pp-item-info">
                  <span className="pp-item-label">Выйти из аккаунта</span>
                  <span className="pp-item-desc">Войти под другим аккаунтом</span>
                </div>
                <ChevronRight size={18} className="pp-item-arrow" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* QR Modal */}
      <AnimatePresence>
        {showQr && (
          <motion.div className="sup-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowQr(false)}>
            <motion.div className="sup-modal" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} onClick={e => e.stopPropagation()}>
              <div className="sup-modal-header">
                <span className="sup-modal-title">Подписка</span>
                <button className="sup-modal-close" onClick={() => setShowQr(false)}><X size={22} /></button>
              </div>
              <div className="sup-modal-body">
                {subLoading ? (
                  <div className="sup-qr-loading">
                    <Loader2 size={32} className="sup-qr-spinner" />
                    <p className="sup-qr-hint">Загрузка...</p>
                  </div>
                ) : !subUrl ? (
                  <p className="sup-qr-hint">Подписка не найдена. Оформите подписку, чтобы получить QR-код.</p>
                ) : (
                  <>
                    <div className="sup-qr-wrap">
                      <QRCodeSVG value={happLink} size={200} bgColor="transparent" fgColor="#ffffff" level="M" />
                    </div>
                    <p className="sup-qr-hint">
                      Отсканируйте QR-код камерой на другом устройстве, чтобы скопировать ссылку подписки
                    </p>
                    <div className="sup-qr-actions">
                      <button className="sup-qr-send" onClick={handleSendSub}>
                        <Send size={18} />
                        Отправить
                      </button>
                      <button className="sup-qr-copy" onClick={handleCopySub}>
                        {copiedSub ? <Check size={18} /> : <Copy size={18} />}
                        {copiedSub ? 'Скопировано' : 'Скопировать'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
